import type { FormDataConvertible } from "@inertiajs/core";
import type { InertiaPrecognitiveFormProps } from "@inertiajs/react";
import { type ReactNode, useEffect, useRef } from "react";
import { firstError } from "@/lib/forms/first-error";

/**
 * When a field is validated against the server.
 * - `submit`  — only on form submit (lazy). For low-value presence/length fields.
 * - `blur`    — on focus loss (default). Pre-submit feedback at a natural pause, no keystroke noise.
 * - `change`  — debounced live from the first keystroke. Only for async availability checks
 *               (e.g. username/handle) where live feedback is genuinely useful.
 *
 * Universal rule across all triggers: once a field has errored it escalates to validate-on-change
 * until it is clean again, so the error clears live as the user corrects it. This falls out of the
 * `fieldError` check in `onChange` below — `form.errors` is populated by both precognition
 * validation and real submit failures.
 */
export type ValidateOn = "submit" | "blur" | "change";

interface BindFieldOptions {
  validateOn?: ValidateOn;
  /** Opt-in positive confirmation. Shown only once the field validates clean pre-submit. Earns its
   *  space only for uncertain/effortful inputs (handle availability), not obvious presence checks. */
  successMessage?: ReactNode;
  /** Sanitises the raw input before it lands in form data (e.g. handle → lowercase, strip illegal
   *  chars). Applied in `onChange` so the persisted/validated value matches what the user sees. */
  transform?: (value: string) => string;
}

interface BoundField {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  isInvalid: boolean;
  errorMessage: string | undefined;
  successMessage: ReactNode | undefined;
}

export interface UsePrecognitionFieldsReturn {
  /** Props bundle for a text input. Spread onto a WizardTextField/StandardTextField. */
  bindField: (field: string, opts?: BindFieldOptions) => BoundField;
  /** First error string for a field, or undefined. For custom/non-text inputs (e.g. radios).
   *  Accepts nested/dotted and foreign keys the form data type doesn't list. */
  fieldError: (field: string) => string | undefined;
  /** True once precognition has validated the field clean on the server AND it is non-empty.
   *  For custom/non-text inputs that drive their own success affordance. */
  fieldValid: (field: string) => boolean;
}

/**
 * Shared precognition field layer. Takes an already-built Inertia precognition form and centralises
 * the validate-on-change/blur/submit decision so a field's behaviour — and flipping it later — is a
 * one-line change at the call site. Composed by `useWizardForm` and used directly by raw-`useForm`
 * modal forms.
 */
export function usePrecognitionFields<TData extends Record<string, FormDataConvertible>>(
  form: InertiaPrecognitiveFormProps<TData>,
): UsePrecognitionFieldsReturn {
  const validate = (field: string) => {
    (form.validate as (f: string) => unknown)(field);
  };

  // Fields wired to validate on change (stable across renders) and the last value we kicked a
  // validation for, so the effect below only re-validates on a real change.
  const changeFields = useRef(new Set<string>());
  const lastValidated = useRef(new Map<string, string>());

  const fieldError = (field: string): string | undefined => {
    const errors = form.errors as unknown as Record<string, string | string[] | undefined>;
    return firstError(errors[field]);
  };

  // Walks a dotted path (e.g. `individual_profile.educator_experience`) into the form data.
  const isNonEmpty = (field: string): boolean => {
    const data = form.data as unknown as Record<string, unknown>;
    const value = field
      .split(".")
      .reduce<unknown>((acc, key) => (acc == null ? acc : (acc as Record<string, unknown>)[key]), data);
    if (value == null) return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  };

  const fieldValid = (field: string): boolean => {
    // The precognition predicates type their field arg as a concrete form key, which dotted/foreign
    // keys don't satisfy through a generic wrapper — erase the generic at this boundary.
    const f = form as unknown as {
      valid: (k: string) => boolean;
      invalid: (k: string) => boolean;
    };
    // Deliberately NOT gated on the global `validating` flag. `valid(field)` holds its last result
    // while precognition re-validates after each keystroke, so reading it directly keeps the success
    // state stable; gating on `validating` made "looks good" flicker off-and-on as the user typed. A
    // still-clean re-validation simply re-affirms valid; one that fails flips `invalid(field)` and
    // hides it.
    if (f.invalid(field) || !f.valid(field)) return false;
    return isNonEmpty(field);
  };

  const readValue = (field: string): string => {
    const data = form.data as unknown as Record<string, unknown>;
    const value = field
      .split(".")
      .reduce<unknown>((acc, key) => (acc == null ? acc : (acc as Record<string, unknown>)[key]), data);
    return typeof value === "string" ? value : "";
  };

  // Live (change-triggered) validation runs HERE, after commit — not inline in `onChange`. Inertia's
  // form reads the value to validate from an internal data ref it refreshes in a post-commit effect,
  // so a `validate()` fired synchronously after `setData` in the same tick reads the PREVIOUS value
  // and lags one keystroke behind (the final character never gets validated — a taken handle whose
  // prefix is free reads as "available" until submit). Running it from this effect guarantees the ref
  // reflects the just-typed value: Inertia's data-ref effect is registered earlier (its `useForm`
  // runs before this hook), so React flushes it first.
  //
  // A field is live while it is change-mode OR currently errored (universal escalate-until-clean).
  // We seed `lastValidated` on first sight so the untouched prefill / already-errored value isn't
  // re-validated, then validate only on an actual change. Clean non-change fields are forgotten so
  // they revert to blur-only.
  const errors = form.errors as unknown as Record<string, unknown>;
  useEffect(() => {
    const live = new Set(changeFields.current);
    for (const field of Object.keys(errors)) live.add(field);
    for (const field of live) {
      const current = readValue(field);
      if (!lastValidated.current.has(field)) {
        lastValidated.current.set(field, current);
        continue;
      }
      if (lastValidated.current.get(field) !== current) {
        lastValidated.current.set(field, current);
        validate(field);
      }
    }
    for (const field of lastValidated.current.keys()) {
      if (!live.has(field)) lastValidated.current.delete(field);
    }
  });

  const bindField = (field: string, opts?: BindFieldOptions): BoundField => {
    const validateOn = opts?.validateOn ?? "blur";
    const setData = form.setData as unknown as (key: string, value: FormDataConvertible) => void;
    const transform = opts?.transform;
    if (validateOn === "change") changeFields.current.add(field);

    return {
      value: readValue(field),
      onChange: (value) => {
        setData(field, transform ? transform(value) : value);
        // Validation is kicked by the post-commit effect above (change-mode + escalation), never
        // inline here — doing it inline would validate the pre-keystroke value.
      },
      onBlur: () => {
        if (validateOn !== "submit") validate(field);
      },
      isInvalid: Boolean(fieldError(field)),
      errorMessage: fieldError(field),
      successMessage:
        opts?.successMessage != null && validateOn !== "submit" && fieldValid(field) ? opts.successMessage : undefined,
    };
  };

  return { bindField, fieldError, fieldValid };
}
